import { useI18n } from 'vue-i18n';
import { computed, ref, watch, onMounted, unref } from 'vue';
import type { Ref } from 'vue';

import { NovaDate, YardTaskStatus, namesToFullName } from '@nova/core';
import { transformApiResponseDataToEditData } from './EventTriggeredEditTaskDrawer.helpers';
import { useNotify } from '@/composables';
import useYard from '@/modules/yard/composables/useYard';
import yardTasksService from '@satellite/services/yard-tasks-service';

type User = {
  id?: string;
  firstName?: string | null;
  lastName?: string | null;
};

// TODO: consolidate types with neutron and circe
type Task = {
  id?: string;
  refNum?: number;
  description?: string;
  assignedTo?: User | null;
  assignedBy?: User;
  createdBy?: User;
  dueDateTime?: string | null;
  createDateTime?: string;
  status?: string;
  type?: string;
  tags?: string[];
  child?: {
    category?: string;
    relatedEntity?: string;
    relatedId?: string;
    relatedObject?: any;
    checklist?: any[];
    assetContainer?: any;
    originSpot?: any;
    destinationSpot?: any;
  };
};

export interface UseYardTaskPresenterOptions {
  taskId?: Ref<string | null | undefined>;
  task?: Ref<Task | null | undefined>;
}

export function useYardTaskPresenter(
  { taskId, task }: UseYardTaskPresenterOptions = {},
  lazy = false
) {
  const yardStore = useYard();
  const { t } = useI18n();
  const notify = useNotify();

  const fetchedTask = ref(null);
  const isLoading = ref(false);

  const currentTask = computed(() => {
    return { ...task?.value, ...fetchedTask.value };
  });

  const transformedTask = computed(() => {
    if (!currentTask.value || !warehouseTimezone.value) {
      return null;
    }

    return transformApiResponseDataToEditData(currentTask.value, warehouseTimezone.value);
  });

  const warehouseTimezone = computed(() => {
    return yardStore.selectedWarehouseRef?.value?.timezone;
  });

  const formatDate = (date: string | null, format: string = 'MMM d, yyyy @ h:mm a') => {
    if (!date) {
      return null;
    }

    try {
      return new NovaDate(date).format(format, { tz: warehouseTimezone.value });
    } catch (error) {
      console.error('Error formatting due date:', error);
      return null;
    }
  };

  const formatPersonName = (
    person?: { firstName?: string | null; lastName?: string | null } | null
  ) => {
    if (!person) {
      return null;
    }

    const { firstName, lastName } = person;
    return namesToFullName(firstName, lastName);
  };

  const formatTaskNumber = (taskNumber?: string | null) => {
    if (!taskNumber) {
      return null;
    }

    return String(taskNumber).padStart(3, '0');
  };

  const formatDueDate = (dueDate?: string | null) => {
    const formattedDueDate = formatDate(dueDate);

    return formattedDueDate || t('yard.pages.yardTasks.messages.noDueDate');
  };

  const formatCreatedAt = (createdAt?: string | null) => {
    const formattedCreatedAt = formatDate(createdAt);

    return formattedCreatedAt || '-';
  };

  const formatAssignedToName = (
    assignee?: { firstName?: string | null; lastName?: string | null } | null
  ) => {
    return formatPersonName(assignee) || t('yard.pages.yardTasks.messages.unassigned');
  };

  const formatCreatedBy = (
    createdBy?: { firstName?: string | null; lastName?: string | null } | null
  ) => {
    return formatPersonName(createdBy) || '-';
  };

  const getStatusIcon = (status?: string | null) => {
    if (status === YardTaskStatus.IN_PROGRESS) {
      return 'mdi-progress-clock';
    }

    if (status === YardTaskStatus.COMPLETED) {
      return 'mdi-check-circle';
    }

    if (status === YardTaskStatus.CANCELLED) {
      return 'mdi-close-circle';
    }

    return 'mdi-progress-helper';
  };

  const getStatusLabel = (status?: string | null) => {
    if (status === YardTaskStatus.IN_PROGRESS) {
      return t('yard.pages.yardTasks.labels.inProgress');
    }

    if (status === YardTaskStatus.COMPLETED) {
      return t('yard.pages.yardTasks.labels.completed');
    }

    if (status === YardTaskStatus.CANCELLED) {
      return t('yard.pages.yardTasks.labels.canceled');
    }

    return t('yard.pages.yardTasks.labels.toDo');
  };

  const taskNumber = computed(() => {
    return formatTaskNumber(currentTask.value?.refNum);
  });

  const taskDescription = computed(() => {
    return currentTask.value?.description || null;
  });

  const taskStatus = computed(() => {
    return currentTask.value?.status || null;
  });

  const assignedToName = computed(() => {
    return formatAssignedToName(currentTask.value?.assignedTo);
  });

  const createdByName = computed(() => {
    return formatCreatedBy(currentTask.value?.createdBy);
  });

  const dueDate = computed(() => {
    return formatDueDate(currentTask.value?.dueDateTime);
  });

  const createdAt = computed(() => {
    return formatCreatedAt(currentTask.value?.createDateTime);
  });

  const statusIcon = computed(() => {
    return getStatusIcon(taskStatus.value);
  });

  const statusLabel = computed(() => {
    return getStatusLabel(taskStatus.value);
  });

  const hasUrgentTag = computed(() => {
    if (!currentTask.value?.tags || !Array.isArray(currentTask.value.tags)) {
      return false;
    }

    return currentTask.value.tags.includes('urgent');
  });

  const taskCategory = computed(() => {
    return currentTask.value?.child?.category || null;
  });

  const relatedEntity = computed(() => {
    return currentTask.value?.child?.relatedEntity || null;
  });

  const relatedId = computed(() => {
    return currentTask.value?.child?.relatedId || null;
  });

  const relatedObject = computed(() => {
    return currentTask.value?.child?.relatedObject || null;
  });

  const fetchTask = async (id: string) => {
    if (!id) {
      return;
    }

    isLoading.value = true;

    try {
      const response = await yardTasksService.getYardTask(id);
      fetchedTask.value = response;
    } catch (error) {
      notify(t('yard.messages.failedToFetchYardTask'), 'error');
      fetchedTask.value = null;
    } finally {
      isLoading.value = false;
    }
  };

  const refresh = () => {
    fetchedTask.value = null;

    const currentTaskIdValue = unref(taskId);
    const currentTaskValue = unref(task);
    const id = currentTaskIdValue || currentTaskValue?.id;

    if (id) {
      fetchTask(id);
    }
  };

  watch([() => unref(taskId), () => unref(task)?.id], () => refresh());

  onMounted(() => {
    if (!lazy && taskId) {
      refresh();
    }
  });

  return {
    isLoading,
    currentTask,
    transformedTask,

    taskNumber,
    taskDescription,
    taskStatus,
    assignedToName,
    createdByName,
    dueDate,
    createdAt,
    statusIcon,
    statusLabel,
    hasUrgentTag,
    taskCategory,
    relatedEntity,
    relatedId,
    relatedObject,

    formatAssignedToName,
    formatCreatedBy,
    formatDueDate,
    formatCreatedAt,
    formatTaskNumber,
    getStatusIcon,
    getStatusLabel,

    fetchTask,
    refresh
  };
}
